import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LayoutDashboard, ShieldCheck, Shield, Bug, X } from "lucide-react";
import { StatsCard } from './StatsCard';
import { DashboardLineChart } from './LineChart';
import { DeploymentImpactTimeline } from './DeploymentImpactTimeline';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { format, addDays } from 'date-fns';
import { Button } from "@/components/ui/button";

interface DateRange {
  from: Date;
  to: Date;
}

interface TrendDataPoint {
  date: string;
  incidents: number;
  tasks: number;
  incidentDetails?: Array<{
    number?: string;
    incident_number?: string;
    opened?: string;
    created?: string;
    closed?: string;
    resolved?: string;
    short_description?: string;
    description?: string;
  }>;
  taskDetails?: Array<{
    number: string;
    opened?: string;
    created?: string;
    closed?: string;
    resolved?: string;
    short_description?: string;
    description?: string;
  }>;
}

interface ExecutiveSummaryProps {
  incidents: any[];
  catalogTasks: any[];
  dateRange?: DateRange;
}

interface SummaryData {
  totalIncidents: number;
  criticalIncidents: number;
  totalTasks: number;
  slaComplianceRate: number;
  avgResolutionTime: number;
  trendData: TrendDataPoint[];
}

export function ExecutiveSummary({ incidents, catalogTasks, dateRange }: ExecutiveSummaryProps) {
  const summaryData = React.useMemo(() => {
    // Calculate key metrics
    const totalIncidents = incidents.length;
    const totalTasks = catalogTasks.length;
    
    const criticalIncidents = incidents.filter(inc => 
      (inc.priority || "").toLowerCase() === "critical" || 
      (inc.priority || "") === "1"
    ).length;
    
    // Calculate SLA compliance
    let compliantIncidents = 0;
    let totalWithResolutionData = 0;
    
    incidents.forEach(incident => {
      const created = new Date(incident.created || incident.opened || "");
      const resolved = new Date(incident.resolved_at || incident.closed_at || "");
      
      if (!isNaN(created.getTime()) && !isNaN(resolved.getTime())) {
        totalWithResolutionData++;
        
        // Calculate resolution time in hours
        const resolutionTime = (resolved.getTime() - created.getTime()) / (1000 * 60 * 60);
        
        // Simplified SLA: critical < 4h, high < 24h, others < 48h
        const priority = (incident.priority || "").toLowerCase();
        let metSla = false;
        
        if ((priority === "critical" || priority === "1") && resolutionTime <= 4) {
          metSla = true;
        } else if ((priority === "high" || priority === "2") && resolutionTime <= 24) {
          metSla = true;
        } else if (resolutionTime <= 48) {
          metSla = true;
        }
        
        if (metSla) compliantIncidents++;
      }
    });
    
    const slaComplianceRate = totalWithResolutionData > 0 
      ? Math.round((compliantIncidents / totalWithResolutionData) * 100) 
      : 0;
    
    // Calculate average resolution time
    let totalResolutionTime = 0;
    let incidentsWithResolution = 0;
    
    incidents.forEach(incident => {
      const created = new Date(incident.created || incident.opened || "");
      const resolved = new Date(incident.resolved_at || incident.closed_at || "");
      
      if (!isNaN(created.getTime()) && !isNaN(resolved.getTime())) {
        totalResolutionTime += (resolved.getTime() - created.getTime()) / (1000 * 60 * 60);
        incidentsWithResolution++;
      }
    });
    
    const avgResolutionTime = incidentsWithResolution > 0 
      ? Math.round(totalResolutionTime / incidentsWithResolution * 10) / 10
      : 0;
    
    // Group by date for the trend chart
    const dateMap = new Map<string, { incidents: number, tasks: number, incidentDetails?: Array<{
      number?: string;
      incident_number?: string;
      opened?: string;
      created?: string;
      closed?: string;
      resolved?: string;
      short_description?: string;
      description?: string;
    }>, taskDetails?: Array<{
      number: string;
      opened?: string;
      created?: string;
      closed?: string;
      resolved?: string;
      short_description?: string;
      description?: string;
    }> }>();
    
    // Process incidents
    incidents.forEach(incident => {
      const date = new Date(incident.created || incident.opened || "");
      if (isNaN(date.getTime())) return;
      
      const dateKey = format(date, 'yyyy-MM-dd');  // Use consistent date format
      
      if (!dateMap.has(dateKey)) {
        dateMap.set(dateKey, { incidents: 0, tasks: 0, incidentDetails: [], taskDetails: [] });
      }
      
      dateMap.get(dateKey)!.incidents++;
      dateMap.get(dateKey)!.incidentDetails = dateMap.get(dateKey)!.incidentDetails || [];
      dateMap.get(dateKey)!.incidentDetails.push({
        number: incident.number,
        incident_number: incident.incident_number,
        opened: incident.opened,
        created: incident.created,
        closed: incident.closed,
        resolved: incident.resolved,
        short_description: incident.short_description,
        description: incident.description
      });
    });
    
    // Process catalog tasks
    catalogTasks.forEach(task => {
      const date = new Date(task.created || task.opened || "");
      if (isNaN(date.getTime())) return;
      
      const dateKey = format(date, 'yyyy-MM-dd');  // Use consistent date format
      
      if (!dateMap.has(dateKey)) {
        dateMap.set(dateKey, { incidents: 0, tasks: 0, incidentDetails: [], taskDetails: [] });
      }
      
      dateMap.get(dateKey)!.tasks++;
      dateMap.get(dateKey)!.taskDetails = dateMap.get(dateKey)!.taskDetails || [];
      dateMap.get(dateKey)!.taskDetails.push({
        number: task.number,
        opened: task.opened,
        created: task.created,
        closed: task.closed,
        resolved: task.resolved,
        short_description: task.short_description,
        description: task.description
      });
    });
    
    // Convert to array and sort by date
    const trendDataBeforeSlice = Array.from(dateMap.entries())
      .map(([date, data]) => ({
        date,
        incidents: data.incidents,
        tasks: data.tasks,
        incidentDetails: data.incidentDetails,
        taskDetails: data.taskDetails
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    console.log('summaryData - trendData before slice:', trendDataBeforeSlice);

    const trendData = trendDataBeforeSlice.slice(-180); // Last 6 months of daily data

    console.log('summaryData - trendData after slice:', trendData);
    
    return {
      totalIncidents,
      criticalIncidents,
      totalTasks,
      slaComplianceRate,
      avgResolutionTime,
      trendData
    };
  }, [incidents, catalogTasks]);

  const [selectedDate, setSelectedDate] = React.useState<string | null>(null);
  const [selectedDataPoint, setSelectedDataPoint] = React.useState<any>(null);
  const [isOpen, setIsOpen] = React.useState(false);

  const handlePointClick = (date: string, dataPoint: any) => {
    console.log('ExecutiveSummary - Received date:', date);
    console.log('ExecutiveSummary - Received dataPoint (from LineChart):', dataPoint);
    
    // Find the full data point from the original trendData based on the clicked date
    const fullDataPoint = summaryData.trendData.find(d => d.date === date);

    if (fullDataPoint) {
      console.log('ExecutiveSummary - Found fullDataPoint:', fullDataPoint);
      setSelectedDate(date);
      setSelectedDataPoint(fullDataPoint);
      setIsOpen(true);
    } else {
      console.warn('ExecutiveSummary - Full data point not found for date:', date);
      setSelectedDate(null);
      setSelectedDataPoint(null);
      setIsOpen(false);
    }
  };

  const handleReset = () => {
    setSelectedDate(null);
    setSelectedDataPoint(null);
    setIsOpen(false);
  };

  const getFilteredTrendData = React.useMemo(() => {
    if (!selectedDate) return summaryData.trendData;

    const selectedIndex = summaryData.trendData.findIndex(d => d.date === selectedDate);
    if (selectedIndex === -1) return summaryData.trendData;

    // Get 2 points before and after the selected point
    const startIndex = Math.max(0, selectedIndex - 2);
    const endIndex = Math.min(summaryData.trendData.length - 1, selectedIndex + 2);
    
    return summaryData.trendData.slice(startIndex, endIndex + 1);
  }, [summaryData.trendData, selectedDate]);

  const getTicketsForDate = (date: string, dataPoint: any) => {
    console.log('getTicketsForDate - Input date:', date);
    console.log('getTicketsForDate - Input dataPoint:', dataPoint);
    if (!dataPoint) return [];

    const targetDate = new Date(date);
    targetDate.setHours(0, 0, 0, 0); // Normalize target date to start of day

    const tickets = [
      ...(dataPoint.incidentDetails || [])
        .filter((inc: any) => {
          const incidentDate = new Date(inc.opened || inc.created || "");
          incidentDate.setHours(0, 0, 0, 0); // Normalize incident date
          return format(incidentDate, 'yyyy-MM-dd') === format(targetDate, 'yyyy-MM-dd');
        })
        .map((inc: any) => ({
          type: 'incident',
          number: inc.number || inc.incident_number,
          opened: inc.opened || inc.created,
          closed: inc.closed || inc.resolved,
          description: inc.short_description || inc.description
        })),
      ...(dataPoint.taskDetails || [])
        .filter((task: any) => {
          const taskDate = new Date(task.opened || task.created || "");
          taskDate.setHours(0, 0, 0, 0); // Normalize task date
          return format(taskDate, 'yyyy-MM-dd') === format(targetDate, 'yyyy-MM-dd');
        })
        .map((task: any) => ({
          type: 'task',
          number: task.number,
          opened: task.opened || task.created,
          closed: task.closed || task.resolved,
          description: task.short_description || task.description
        }))
    ];

    console.log('getTicketsForDate - Generated tickets:', tickets);
    return tickets.sort((a, b) => new Date(b.opened).getTime() - new Date(a.opened).getTime());
  };

  const trendData = React.useMemo(() => {
    const dateMap = new Map<string, TrendDataPoint>();

    // Initialize dates in range
    let currentDate = dateRange?.from || new Date();
    let endDate = dateRange?.to || new Date();
    while (currentDate <= endDate) {
      const dateKey = format(currentDate, 'yyyy-MM-dd');
      dateMap.set(dateKey, {
        date: dateKey,
        incidents: 0,
        tasks: 0,
        incidentDetails: [],
        taskDetails: []
      });
      currentDate = addDays(currentDate, 1);
    }

    // Process incidents
    incidents.forEach(incident => {
      const date = new Date(incident.opened || incident.created || "");
      if (date >= dateRange?.from && date <= dateRange?.to) {
        const dateKey = format(date, 'yyyy-MM-dd');
        const dayData = dateMap.get(dateKey);
        if (dayData) {
          dayData.incidents++;
          dayData.incidentDetails = dayData.incidentDetails || [];
          dayData.incidentDetails.push({
            number: incident.number,
            incident_number: incident.incident_number,
            opened: incident.opened,
            created: incident.created,
            closed: incident.closed,
            resolved: incident.resolved,
            short_description: incident.short_description,
            description: incident.description
          });
        }
      }
    });

    // Process tasks
    catalogTasks.forEach(task => {
      const date = new Date(task.opened || task.created || "");
      if (date >= dateRange?.from && date <= dateRange?.to) {
        const dateKey = format(date, 'yyyy-MM-dd');
        const dayData = dateMap.get(dateKey);
        if (dayData) {
          dayData.tasks++;
          dayData.taskDetails = dayData.taskDetails || [];
          dayData.taskDetails.push({
            number: task.number,
            opened: task.opened,
            created: task.created,
            closed: task.closed,
            resolved: task.resolved,
            short_description: task.short_description,
            description: task.description
          });
        }
      }
    });

    return Array.from(dateMap.values());
  }, [incidents, catalogTasks, dateRange]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-2xl flex items-center gap-2">
            <LayoutDashboard className="h-6 w-6" />
            Executive Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Ticket Trends</h3>
              {selectedDate && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleReset}
                  className="flex items-center gap-2"
                >
                  <X className="h-4 w-4" />
                  Reset View
                </Button>
              )}
            </div>
            <DashboardLineChart
              data={getFilteredTrendData}
              lines={[
                { dataKey: "incidents", stroke: "#ef4444", name: "Incidents" },
                { dataKey: "tasks", stroke: "#3b82f6", name: "Tasks" }
              ]}
              xAxisKey="date"
              showGrid={true}
              xAxisCount={5}
              formatDate={true}
              dateInterval={1}
              onPointClick={handlePointClick}
            />
          </div>

          <Collapsible
            open={isOpen}
            onOpenChange={setIsOpen}
            className="mt-4"
          >
            <CollapsibleTrigger className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
              <ChevronDown className={cn("h-4 w-4 transition-transform", isOpen ? "transform rotate-180" : "")} />
              {selectedDate ? `Tickets from ${format(new Date(selectedDate), 'MMM d')}` : "Select a point on the graph to view tickets"}
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2">
              {selectedDate && selectedDataPoint && (
                <div className="space-y-2">
                  {getTicketsForDate(selectedDate, selectedDataPoint).map((ticket, index) => (
                    <div
                      key={`${ticket.type}-${ticket.number}-${index}`}
                      className="p-3 rounded-lg border bg-card"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium">
                          {ticket.number}
                        </span>
                        <div className="text-sm text-muted-foreground space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">Raised:</span>
                            <span>{new Date(ticket.opened).toLocaleDateString()}</span>
                          </div>
                          {ticket.closed && (
                            <div className="flex items-center gap-2">
                              <span className="font-medium">Closed:</span>
                              <span>{new Date(ticket.closed).toLocaleDateString()}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {ticket.description}
                      </p>
                    </div>
                  ))}
                  {getTicketsForDate(selectedDate, selectedDataPoint).length === 0 && (
                    <div className="text-center py-4 text-muted-foreground">
                      No tickets found in this date range
                    </div>
                  )}
                </div>
              )}
            </CollapsibleContent>
          </Collapsible>
        </CardContent>
      </Card>

      <Card className="w-full">
        <CardContent className="p-6">
          <DeploymentImpactTimeline 
            incidents={incidents}
            catalogTasks={catalogTasks}
            dateRange={dateRange}
          />
        </CardContent>
      </Card>
    </div>
  );
}

